<template>
  <!-- The settings (SPEC §4.6): module sections first (they don't change the board), then each
       setting a row, its choices side by side (the one in effect highlighted), or − and + for a size. -->
  <div>
    <slot/>
    <div v-for="row in rows" :key="row.tag" class="setrow" role="group" :aria-label="t(row.tag)">
      <span class="label">{{ t(row.tag) }}</span>
      <span v-if="row.choices" class="seg">
        <button v-for="[value, tag] in row.choices" :key="tag" type="button" :class="{ on: get(row) === value }"
                :aria-pressed="get(row) === value ? 'true' : 'false'" @click="put(row, value)">{{ t(tag) }}</button>
      </span>
      <span v-else class="step">
        <button type="button" :aria-label="'−'" :disabled="get(row) <= row.min" @click="put(row, get(row) - 1)">−</button>
        <span class="value">{{ get(row) }}</span>
        <button type="button" :aria-label="'+'" :disabled="get(row) >= row.max" @click="put(row, get(row) + 1)">+</button>
      </span>
    </div>
  </div>
</template>

<script>
import { deepAssign } from '@/utils';
import { t } from '@/texts';

// The stored settings without values never set (null), so that the defaults apply to them.
function withoutUnset(obj) {
  const out = {};
  for (const k in obj || {}) {
    if (obj[k] === null || obj[k] === undefined) continue;
    out[k] = obj[k] instanceof Object ? withoutUnset(obj[k]) : obj[k];
  }
  return out;
}

export default {
  name: 'UISettings',

  computed: {
    ui() {
      // A value never set shows, and acts as, its default (SPEC §4.6).
      const fallback = {
        playMode: 'button',
        soundEffect: 1,
        board: {
          coordinates: 0,
          coordsFontSize: 0,
          padding: 0,
          moveNumbers: 0,
          markFontSize: 0,
          markFontBold: 0,
          lastMoveShape: 'triangle',
          lastMoveColour: 'red',
        }
      };

      return deepAssign(fallback, withoutUnset(this.$store.state.ui));
    },

    // In this order: the two that don't show on the board first, then those that do, so that the
    // board's rows sit right above the preview.
    rows() {
      return [
        { key: 'soundEffect', tag: 'setting-sound', choices: [[1, 'value-on'], [0, 'value-off']] },
        { key: 'playMode', tag: 'setting-play-mode', choices: [['button', 'value-play-button'], ['quick', 'value-play-quick']] },
        { board: 'coordinates', tag: 'setting-coordinates', choices: [[0, 'value-hidden'], [1, 'value-one-side'], [2, 'value-both-sides']] },
        { board: 'moveNumbers', tag: 'setting-move-numbers', choices: [[0, 'value-hidden'], [1, 'value-last-move'], [5, 'value-last-5'], [10, 'value-last-10'], ['all', 'value-all']] },
        { board: 'markFontBold', tag: 'setting-mark-bold', choices: [[0, 'value-normal'], [1, 'value-bold']] },
        { board: 'padding', tag: 'setting-padding', min: -3, max: 4 },
        { board: 'coordsFontSize', tag: 'setting-coords-font', min: -3, max: 4 },
        { board: 'markFontSize', tag: 'setting-mark-font', min: -3, max: 6 },
        { board: 'lastMoveShape', tag: 'setting-last-move-shape', choices: [['triangle', 'value-triangle'], ['circle', 'value-circle']] },
        { board: 'lastMoveColour', tag: 'setting-last-move-colour', choices: [['red', 'value-red'], ['inverse', 'value-inverse']] },
      ];
    },
  },

  methods: {
    t,

    get(row) {
      return row.board ? this.ui.board[row.board] : this.ui[row.key];
    },

    put(row, value) {
      if(row.board) this.set('board', { [row.board]: value });
      else this.set(row.key, value);
    },

    set(key, value) {
      this.$store.commit('set', { ui: { [key]: value } });
      localStorage.setItem('ui', JSON.stringify(this.$store.state.ui));
    },
  }
}
</script>

<style scoped>
.setrow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 8px;
  min-height: 48px;
  padding: 6px 0;
  box-sizing: border-box;
  border-bottom: 1px solid #eee;
  font-size: 15px;
  text-align: left;
}

/* The choices, side by side; the one in effect amber. */
.seg {
  display: inline-flex;
  margin-left: auto;
  border: 1px solid #c9b48a;
  border-radius: 8px;
  overflow: hidden;
}

.seg button {
  padding: 6px 10px;
  border: none;
  border-left: 1px solid #eadfc6;
  background: #fff;
  color: #2c3e50;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
}

.seg button:first-child {
  border-left: none;
}

.seg button.on {
  background: #ffd39a;
  font-weight: bold;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.step button {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #c9b48a;
  border-radius: 16px;
  background: #fff;
  color: #2c3e50;
  font-size: 16px;
  cursor: pointer;
}

.step button:disabled {
  color: #ccc;
  cursor: default;
}

.step .value {
  min-width: 1.5em;
  text-align: center;
}
</style>
